/**
 * MessageSelectionContext
 * Context per gestire la selezione messaggi per l'analisi IA
 */

import { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import type { Message } from '@/lib/types/whatsappChat';

interface MessageSelectionContextType {
  // Stato
  isSelectionMode: boolean;
  selectedMessageIds: Set<string>;
  lastSelectedId: string | null;

  // Azioni
  enterSelectionMode: () => void;
  exitSelectionMode: () => void;
  toggleMessageSelection: (messageId: string, messages: Message[], shiftKey: boolean) => void;
  clearSelection: () => void;
  selectAll: (messages: Message[]) => void;
  getSelectedMessages: (messages: Message[]) => Message[];
}

const MessageSelectionContext = createContext<MessageSelectionContextType | null>(null);

export function MessageSelectionProvider({ children }: { children: ReactNode }) {
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState<Set<string>>(new Set());
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);

  const enterSelectionMode = useCallback(() => {
    setIsSelectionMode(true);
    setSelectedMessageIds(new Set());
    setLastSelectedId(null);
  }, []);

  const exitSelectionMode = useCallback(() => {
    setIsSelectionMode(false);
    setSelectedMessageIds(new Set());
    setLastSelectedId(null);
  }, []);

  const toggleMessageSelection = useCallback((
    messageId: string,
    messages: Message[],
    shiftKey: boolean
  ) => {
    setSelectedMessageIds(prev => {
      const newSet = new Set(prev);

      // Se Shift è premuto e c'è un ultimo messaggio selezionato, seleziona il range
      if (shiftKey && lastSelectedId) {
        const messageIds = messages.map(m => m.id);
        const lastIndex = messageIds.indexOf(lastSelectedId);
        const currentIndex = messageIds.indexOf(messageId);

        if (lastIndex !== -1 && currentIndex !== -1) {
          const startIndex = Math.min(lastIndex, currentIndex);
          const endIndex = Math.max(lastIndex, currentIndex);

          // Seleziona tutti i messaggi nel range
          for (let i = startIndex; i <= endIndex; i++) {
            newSet.add(messageIds[i]);
          }
          return newSet;
        }
      }

      // Toggle normale
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }

      return newSet;
    });

    setLastSelectedId(messageId);
  }, [lastSelectedId]);

  const clearSelection = useCallback(() => {
    setSelectedMessageIds(new Set());
    setLastSelectedId(null);
  }, []);

  const selectAll = useCallback((messages: Message[]) => {
    setSelectedMessageIds(new Set(messages.map(m => m.id)));
  }, []);

  const getSelectedMessages = useCallback((messages: Message[]): Message[] => {
    return messages.filter(m => selectedMessageIds.has(m.id));
  }, [selectedMessageIds]);

  const value = useMemo(() => ({
    isSelectionMode,
    selectedMessageIds,
    lastSelectedId,
    enterSelectionMode,
    exitSelectionMode,
    toggleMessageSelection,
    clearSelection,
    selectAll,
    getSelectedMessages,
  }), [
    isSelectionMode,
    selectedMessageIds,
    lastSelectedId,
    enterSelectionMode,
    exitSelectionMode,
    toggleMessageSelection,
    clearSelection,
    selectAll,
    getSelectedMessages,
  ]);

  return (
    <MessageSelectionContext.Provider value={value}>
      {children}
    </MessageSelectionContext.Provider>
  );
}

export function useMessageSelection() {
  const context = useContext(MessageSelectionContext);
  if (!context) {
    throw new Error('useMessageSelection must be used within MessageSelectionProvider');
  }
  return context;
}
